import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { premiumBubbleStyle, premiumWhiteCardStyle } from './allureCardStyles';
import { PopupPortal } from './PopupPortal';
import { useAnchorRect } from '../hooks/useAnchorRect';

const font = "'Inria Serif', serif";

const InfoIcon = () => (
  <svg width="25" height="25" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 18.75V12M12 7.5V5.25M23.25 12C23.25 18.2132 18.2132 23.25 12 23.25C5.7868 23.25 0.75 18.2132 0.75 12C0.75 5.7868 5.7868 0.75 12 0.75C18.2132 0.75 23.25 5.7868 23.25 12Z" stroke="var(--text-2)" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

export const GuestDiscountSummaryCard = ({ discount }) => {
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const buttonRect = useAnchorRect(buttonRef, isInfoOpen);

  useEffect(() => {
    if (!isInfoOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsInfoOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isInfoOpen]);

  return (
    <div ref={wrapperRef} style={{ width: '100%', maxWidth: 360, position: 'relative' }}>
      <div
        data-testid="guest-home-current-discount-card"
        style={{
          ...premiumWhiteCardStyle,
          width: '100%',
          padding: '16px 18px',
          display: 'grid',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <p data-testid="guest-home-current-discount-text" style={{ fontFamily: font, fontSize: 22, fontWeight: 700, color: 'var(--status-success-text)', lineHeight: 1.05, margin: 0 }}>
            Current discount: {discount}%
          </p>
          <button
            ref={buttonRef}
            type="button"
            data-testid="guest-home-current-discount-info-button"
            aria-label="Discount info"
            onClick={() => setIsInfoOpen((currentValue) => !currentValue)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <InfoIcon />
          </button>
        </div>

        <p data-testid="guest-home-current-discount-helper" style={{ fontFamily: font, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.2, margin: 0 }}>
          Unlock higher discounts as you spend.
        </p>
      </div>

      <AnimatePresence>
        {isInfoOpen && buttonRect ? (
          <PopupPortal>
            <div
              style={{
                position: 'fixed',
                top: buttonRect.bottom + 12,
                right: Math.max(20, window.innerWidth - buttonRect.right - 8),
                width: 'min(280px, calc(100vw - 40px))',
              }}
            >
              <motion.div
                aria-hidden="true"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                style={{
                  width: 18,
                  height: 18,
                  position: 'absolute',
                  top: -8,
                  right: 14,
                  transform: 'rotate(45deg)',
                  ...premiumBubbleStyle,
                  borderBottom: 'none',
                  borderRight: 'none',
                }}
              />
              <motion.div
                data-testid="guest-home-current-discount-info-bubble"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                style={{
                  ...premiumBubbleStyle,
                  width: '100%',
                  padding: '14px 16px',
                  display: 'grid',
                  gap: 6,
                  position: 'relative',
                }}
              >
                <p data-testid="guest-home-current-discount-info-line-one" style={{ fontFamily: font, fontSize: 16, color: 'var(--text)', lineHeight: 1.2, margin: 0 }}>
                  Stay active to keep your discount level.
                </p>
                <p data-testid="guest-home-current-discount-info-line-two" style={{ fontFamily: font, fontSize: 16, color: 'var(--text)', lineHeight: 1.2, margin: 0 }}>
                  2-year inactivity may reduce it.
                </p>
              </motion.div>
            </div>
          </PopupPortal>
        ) : null}
      </AnimatePresence>
    </div>
  );
};